import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { EM_DASH, formatCount, formatMiles, formatProvenance, sortLineRows } from "./structureMilesUtils";

const VOLTAGE_BIN_TOOLTIP =
    "Bins are labelled by their lower edge -- the 69kV row covers 69kV up to but not including 115kV.";

const UNRESOLVED_TOOLTIP =
    "These spans were skipped because an endpoint structure could not be located in the root-geojson. The totals shown exclude them.";

const LINE_COLUMNS = [
    { key: "line_name", label: "Line", align: "left" },
    { key: "voltage_kv", label: "kV", align: "right" },
    { key: "structure_miles", label: "Structure Miles", align: "right" },
    { key: "circuit_miles", label: "Circuit Miles", align: "right" },
    { key: "unique_span_count", label: "Unique Spans", align: "right" },
    { key: "total_span_count", label: "Total Spans", align: "right" },
];

function TotalsStat({ label, value, warning }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", minWidth: "120px" }}>
            <Typography variant="caption" color="textSecondary">
                {label}
            </Typography>
            <Typography variant="subtitle1" sx={warning ? { color: "warning.main" } : undefined}>
                {value}
            </Typography>
        </div>
    );
}

function VoltageTable({ rows }) {
    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="textSecondary">
                No voltage breakdown in this dataset.
            </Typography>
        );
    }
    return (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Tooltip title={VOLTAGE_BIN_TOOLTIP}>
                                <span>Nominal Voltage Bin</span>
                            </Tooltip>
                        </TableCell>
                        <TableCell align="right">Structure Miles</TableCell>
                        <TableCell align="right">Circuit Miles</TableCell>
                        <TableCell align="right">Unique Spans</TableCell>
                        <TableCell align="right">Total Spans</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* Rendered in payload order and deliberately not sortable. The backend emits these
                        bins in a fixed ascending order, only for the bins that occur, so a client-side
                        sort would scramble it into 115kV, 138kV, 69kV. */}
                    {rows.map((row) => (
                        <TableRow key={row.voltage_bin}>
                            <TableCell>{row.voltage_bin}</TableCell>
                            <TableCell align="right">{formatMiles(row.structure_miles)}</TableCell>
                            <TableCell align="right">{formatMiles(row.circuit_miles)}</TableCell>
                            <TableCell align="right">{formatCount(row.unique_span_count)}</TableCell>
                            <TableCell align="right">{formatCount(row.total_span_count)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function LineTable({ rows, sort, onSort }) {
    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="textSecondary">
                No per-line breakdown in this dataset.
            </Typography>
        );
    }
    return (
        // Capped and sticky-headed because by_line has one row per line and can run past a hundred.
        <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {LINE_COLUMNS.map((column) => (
                            <TableCell key={column.key} align={column.align}>
                                <TableSortLabel
                                    active={sort.key === column.key}
                                    direction={sort.key === column.key ? sort.direction : "asc"}
                                    onClick={() => onSort(column.key)}
                                >
                                    {column.label}
                                </TableSortLabel>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => (
                        // line_name can be "", so it is not a safe key on its own.
                        <TableRow key={`${row.line_name}||${index}`}>
                            <TableCell>
                                {row.line_name ? (
                                    row.line_name
                                ) : (
                                    <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                                        (unnamed)
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell align="right">{formatCount(row.voltage_kv)}</TableCell>
                            <TableCell align="right">{formatMiles(row.structure_miles)}</TableCell>
                            <TableCell align="right">{formatMiles(row.circuit_miles)}</TableCell>
                            <TableCell align="right">{formatCount(row.unique_span_count)}</TableCell>
                            <TableCell align="right">{formatCount(row.total_span_count)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default function StructureMilesBreakdownsDialog({ open, onClose, dataset, meta, client, project }) {
    // The default reproduces the backend's own sorted(by_line.items()) order, so opening the
    // dialog shows exactly what the payload contains before the user reorders anything.
    const [sort, setSort] = useState({ key: "line_name", direction: "asc" });

    const handleSort = (key) => {
        setSort((current) =>
            current.key === key
                ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
                : { key, direction: key === "line_name" ? "asc" : "desc" }
        );
    };

    const voltageRows = Array.isArray(dataset?.by_voltage) ? dataset.by_voltage : [];
    const lineRows = sortLineRows(dataset?.by_line, sort.key, sort.direction);
    const provenance = formatProvenance(meta);
    const unresolved = dataset?.unresolved_span_count;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Structure Miles {EM_DASH} {client} / {project}
            </DialogTitle>
            <DialogContent dividers>
                {!dataset ? (
                    <Typography variant="body2" color="textSecondary">
                        No structure miles data loaded.
                    </Typography>
                ) : (
                    <>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", marginBottom: "8px" }}>
                            <TotalsStat label="Structure Miles" value={formatMiles(dataset.total_structure_miles)} />
                            <TotalsStat label="Circuit Miles" value={formatMiles(dataset.total_circuit_miles)} />
                            <TotalsStat label="Unique Spans" value={formatCount(dataset.unique_span_count)} />
                            <TotalsStat label="Total Spans" value={formatCount(dataset.total_span_count)} />
                            {unresolved > 0 && (
                                <Tooltip title={UNRESOLVED_TOOLTIP}>
                                    <div>
                                        <TotalsStat label="Unresolved Spans" value={formatCount(unresolved)} warning />
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                        {provenance && (
                            <Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic" }}>
                                {provenance}
                            </Typography>
                        )}

                        <Typography variant="subtitle2" style={{ marginTop: "16px", marginBottom: "4px" }}>
                            By Voltage
                        </Typography>
                        <VoltageTable rows={voltageRows} />

                        <Typography variant="subtitle2" style={{ marginTop: "16px", marginBottom: "4px" }}>
                            By Line
                        </Typography>
                        <LineTable rows={lineRows} sort={sort} onSort={handleSort} />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
